import type { ShallowRef } from 'vue';

import type OLMap from 'ol/Map';

import type { ServiceKey } from '@/utils/types/baseStationsTypes';
import type { GeoFencingRaw } from '@/api/ApiTypes';

import MapFeatures from '@/components/map/features/MapFeatures';
import type { MapFeature } from '@/components/map/features/MapFeatures';
import type { QualityColorMode } from '@/components/map/consts/MapConsts';
import { MAP_MODE } from '@/components/map/consts/mapFeatureConsts';
import type {
	BaseStationFeatureItem,
	BaseStationFeatureItemWithSubnetworkColors,
	BaselineWithCoords,
	CoverageContourFeature,
	GridpointDisplay,
	MapMode,
	ProfileFeatureItem,
	FeatureData,
	ActiveSubNetworkWithColor,
	QualityRectangleFeature,
	RoverHistoryCurrentDisplay,
	RoverHistoryFragmentDisplay,
	VRSParticipantRoverDisplay,
} from '@/components/map/types/mapFeatureTypes';
import { getSubNetworkIds } from '@/components/map/utils/mapFeatureGuards';

import type { CcmGridpointDisplay, CcmOtherGridpointDisplay } from '@/components/map/cnetConstructorMode/types/ccmTypes';


type BsFilterSelected = 'all' | ServiceKey;

type SubNetworksGeojson = {
	coverage: CoverageContourFeature[][];
	quality: QualityRectangleFeature[][];
};


type MapSourceFeatureGroupContext = {
	mode: MapMode;
	doFit: boolean;
	mapInstance: OLMap;
	gridpoints: GridpointDisplay[];
	baseStations: BaseStationFeatureItem[];
	roversActive: VRSParticipantRoverDisplay[];
	roversFailed: VRSParticipantRoverDisplay[];
	profiles: ProfileFeatureItem[];
	subNetworksGeojson: SubNetworksGeojson;
	baselines: BaselineWithCoords[];
	currentRoversSelectedFeatures: RoverHistoryCurrentDisplay[];
	roversHistoryPaths: RoverHistoryFragmentDisplay[][];
	roversHistoryAutoUpdate: boolean;
	bsFilterSelected: BsFilterSelected;
	fillCoverageArea: boolean;
	qualityColorMode: QualityColorMode;
	focusedMountpoint: string;
	allSubnetworksMode: boolean;
	allowToSeeMountpointNames: boolean;
	geoFencing: GeoFencingRaw | null;
	activeSubNetworks: ActiveSubNetworkWithColor[];
	anchorPoint: { lat: number; lon: number } | null;
	ccmGridpoints: CcmGridpointDisplay[];
	ccmOtherGridpoints: CcmOtherGridpointDisplay[];
	hoveredFeatureData: FeatureData | null;
	baseStationFeatures: ShallowRef<MapFeature[]>;
	roversHistoryPathsFeatures: ShallowRef<MapFeature[]>;
};


type MapSourceFeatureGroup = {
	when: boolean;
	getFeatures: (ctx: MapSourceFeatureGroupContext) => MapFeature[];
};


const getBsWithSnColors = (
	baseStations: BaseStationFeatureItem[],
	activeSubNetworks: ActiveSubNetworkWithColor[],
): BaseStationFeatureItemWithSubnetworkColors[] => baseStations.map(item => ({
	...item,
	featureData: {
		...item.featureData,
		colors: getSubNetworkIds(item.featureData).map(id => {
			const subNetwork = activeSubNetworks.find(sn => sn.id === id);
			return subNetwork != null ? subNetwork.color : '';
		}),
	},
}));


const getGridpointsFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => (
	MapFeatures.createGridpointsFeatures(ctx.gridpoints)
);


const getRoversHistoryCurrentFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => (
	MapFeatures.createRoverHistoryCurrentFeatures(ctx.currentRoversSelectedFeatures)
);


const getRoversHistoryPathsFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	const size = ctx.mapInstance.getSize();
	const viewportExtent = (!ctx.doFit && size != null)
		? ctx.mapInstance.getView().calculateExtent(size)
		: null;
	const pathFeatures: MapFeature[] = [];
	ctx.roversHistoryPaths.forEach(item => {
		pathFeatures.push(
			...MapFeatures.createRoverHistoryFragmentsFeatures(
				item,
				ctx.roversHistoryAutoUpdate,
				viewportExtent,
			),
		);
	});
	ctx.roversHistoryPathsFeatures.value = pathFeatures;
	return pathFeatures;
};


const clearRoverPathsFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	ctx.roversHistoryPathsFeatures.value = [];
	return [];
};


const getCoverageLayersFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => [
	...MapFeatures.createCoverageContourFeatures(ctx.subNetworksGeojson.coverage),
	...MapFeatures.createQualityRectangleFeatures(
		ctx.subNetworksGeojson.quality,
		ctx.qualityColorMode,
	),
];


const getMainCoverageRoversFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	const features: MapFeature[] = [
		...MapFeatures.createLinksFeatures(ctx.roversActive),
		...MapFeatures.createActiveGettersFeatures(ctx.roversActive),
		...MapFeatures.createFailedGettersFeatures(ctx.roversFailed),
	];
	if (ctx.bsFilterSelected === 'all' || ctx.bsFilterSelected === 'vrs') {
		features.push(
			...MapFeatures.createVirtualBSFeatures(ctx.roversActive),
			...MapFeatures.createVirtualBSCircleFeatures(
				ctx.roversActive,
				ctx.fillCoverageArea,
			),
		);
	}
	features.push(...MapFeatures.createProfilesFeatures(ctx.profiles));
	return features;
};


const getBaseStationsFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	const features = MapFeatures.createBaseStationsFeatures(
		ctx.baseStations,
		null,
		ctx.mode,
		ctx.focusedMountpoint,
	);
	ctx.baseStationFeatures.value = features;
	return features;
};


const getSubnetworkCirclesFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	const bsWithSNColors = getBsWithSnColors(ctx.baseStations, ctx.activeSubNetworks);
	return MapFeatures.createBaseStationsSubnetworkCirclesFeatures(bsWithSNColors);
};


const getBaseStationCirclesFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => (
	MapFeatures.createBaseStationsCircleFeatures(
		ctx.baseStations,
		ctx.fillCoverageArea,
	)
);


const getBaseStationLabelsFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => (
	MapFeatures.createBaseStationsLabelFeatures(
		ctx.baseStations,
		ctx.focusedMountpoint,
		ctx.mode,
		ctx.fillCoverageArea,
	)
);


const getGeoFencingFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	if (ctx.geoFencing == null) {
		return [];
	}
	return MapFeatures.createGeoFencingCircleFeature(ctx.geoFencing);
};


const getBaselinesFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	const features: MapFeature[] = [
		...MapFeatures.createBaselineLinksFeatures(ctx.baselines),
	];
	const usedIds: number[] = [];
	for (const item of ctx.baselines) {
		usedIds.push(item.base.id);
		usedIds.push(item.rover.id);
	}
	const usedBaseStations = ctx.baseStations.filter(
		item => usedIds.includes(item.featureData.id),
	);
	const bsFeatures = MapFeatures.createBaseStationsFeatures(
		usedBaseStations,
		null,
		MAP_MODE.main,
		ctx.focusedMountpoint,
	);
	ctx.baseStationFeatures.value = bsFeatures;
	features.push(
		...bsFeatures,
		...MapFeatures.createBaseStationsLabelFeatures(
			usedBaseStations,
			ctx.focusedMountpoint,
			ctx.mode,
			ctx.fillCoverageArea,
		),
	);
	return features;
};


const getCnetConstructorFeatures = (ctx: MapSourceFeatureGroupContext): MapFeature[] => {
	const features: MapFeature[] = [];
	if (ctx.anchorPoint != null) {
		features.push(
			...MapFeatures.createAnchorPointFeature(
				ctx.anchorPoint.lat,
				ctx.anchorPoint.lon,
			),
		);
	}
	const hoveredLabel = ctx.hoveredFeatureData != null
		? Reflect.get(ctx.hoveredFeatureData, 'label')
		: null;
	ctx.ccmGridpoints.forEach(item => {
		features.push(
			...MapFeatures.createCnetConstructorGridpointFeature(
				item.lat,
				item.lon,
				item.label,
				item.selected,
				item.gpIndex,
				typeof hoveredLabel === 'string' && hoveredLabel === item.label,
			),
		);
	});
	ctx.ccmOtherGridpoints.forEach(item => {
		features.push(
			...MapFeatures.createCnetConstructorOtherGridpointFeature(
				item.lat,
				item.lon,
				item.label,
				item.color,
			),
		);
	});
	return features;
};


const buildMapSourceFeatureGroups = (ctx: MapSourceFeatureGroupContext): MapSourceFeatureGroup[] => {
	const { mode } = ctx;
	const mainOrCoverageModes: readonly MapMode[] = [
		MAP_MODE.main,
		MAP_MODE.coverage,
	];
	const showBaseStationOlLabels = ctx.allowToSeeMountpointNames
		&& mode !== MAP_MODE.baselines
		&& !(mode === MAP_MODE.coverage && !ctx.allSubnetworksMode);

	return [
		{
			when: mode === MAP_MODE.gnssPerGridpoint,
			getFeatures: getGridpointsFeatures,
		},
		{
			when: mode === MAP_MODE.roversHistory,
			getFeatures: getRoversHistoryCurrentFeatures,
		},
		{
			when: mode === MAP_MODE.roversHistory,
			getFeatures: getRoversHistoryPathsFeatures,
		},
		{
			when: mode !== MAP_MODE.roversHistory,
			getFeatures: clearRoverPathsFeatures,
		},
		{
			when: mode === MAP_MODE.coverage,
			getFeatures: getCoverageLayersFeatures,
		},
		{
			when: mainOrCoverageModes.includes(mode),
			getFeatures: getMainCoverageRoversFeatures,
		},
		{
			when: mode !== MAP_MODE.baselines,
			getFeatures: getBaseStationsFeatures,
		},
		{
			when: mode === MAP_MODE.subNetworks,
			getFeatures: getSubnetworkCirclesFeatures,
		},
		{
			when: mode !== MAP_MODE.baselines
				&& mode !== MAP_MODE.cnetConstructor
				&& mode !== MAP_MODE.subNetworks,
			getFeatures: getBaseStationCirclesFeatures,
		},
		{
			when: showBaseStationOlLabels,
			getFeatures: getBaseStationLabelsFeatures,
		},
		{
			when: ctx.geoFencing != null,
			getFeatures: getGeoFencingFeatures,
		},
		{
			when: mode === MAP_MODE.baselines,
			getFeatures: getBaselinesFeatures,
		},
		{
			when: mode === MAP_MODE.cnetConstructor,
			getFeatures: getCnetConstructorFeatures,
		},
	];
};


const collectMapSourceFeatures = (
	ctx: MapSourceFeatureGroupContext,
): MapFeature[] => buildMapSourceFeatureGroups(ctx)
	.filter(group => group.when)
	.flatMap(group => group.getFeatures(ctx));


export type {
	BsFilterSelected,
	MapSourceFeatureGroupContext,
};

export {
	collectMapSourceFeatures,
};
